/** @format */

import React, { useCallback } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { makeStyles } from '@material-ui/core/styles';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { LinearBillingUploadReportMessage } from '../../../interfaces/billing/LinearBillingUploadReportMessage';
import TemplateButton                               from "../../../components/Buttons/TemplateButton";
import {PanoramaFishEye, Report, Visibility, Watch} from "@material-ui/icons";
import {PRICELIST_UPLOAD_STATUS}                    from "../../../modules/pricelists/helpers/PricelistUploadHelper";
import {PricelistUploadFileView} from "./PricelistUploadFileView";
import TariffInterface                                       from "../../../interfaces/billing/TariffInterface";
import CustomTextButtom from "../../../components/Buttons/CustomTextButton";

const useStyle = makeStyles(adescomStyle);

interface PricelistUploadReportProps {
    open: boolean;
    report: LinearBillingUploadReportMessage;
    tariff: TariffInterface;
    importId: number | undefined;
    closeDialog: () => void;
}

export const PricelistUploadReport = (props: PricelistUploadReportProps): React.ReactElement => {
    const classes = useStyle();

    const report = props.report;
    const [openFileView, setOpenFileView] = React.useState<boolean>(false);

    const open = props.open;
    const closeDialog = props.closeDialog;
    const tariff = props.tariff;
    const importId = props.importId;

    const onClose = useCallback((): void => {
        closeDialog();
    }, [closeDialog]);

    return (
        <>
            <Dialog
                aria-labelledby="alert-dialog-slide-title"
                aria-describedby="alert-dialog-slide-description"
                open={open}
                onClose={onClose}
                maxWidth="md"
            >
                <DialogTitle>
                    <I18n trans={'pricelists:import_report.report'} />
                    {importId && (
                        <TemplateButton
                            icon={Visibility}
                            callback={(): void => {
                                setOpenFileView(true);
                            }}
                            callbackParam={null}
                            tooltipTitle={'pricelists:import_report.view_file'}
                        />
                    )}
                </DialogTitle>
                <DialogContent>
                    <GridContainer>
                        <GridItem xs={11}>
                            <I18n trans={'pricelists:import_report.added_records'} />
                        </GridItem>
                        <GridItem xs={1}>{report.addedRecords}</GridItem>
                        <GridItem xs={11}>
                            <I18n trans={'pricelists:import_report.modified_records'} />
                        </GridItem>
                        <GridItem xs={1}>{report.modifiedRecords}</GridItem>
                        <GridItem xs={11}>
                            <I18n trans={'pricelists:import_report.removed_records'} />
                        </GridItem>
                        <GridItem xs={1}>{report.removedRecords}</GridItem>
                        <GridItem xs={11}>
                            <I18n trans={'pricelists:import_report.rejected_lines'} />
                        </GridItem>
                        <GridItem xs={1}>{report.rejectedLines}</GridItem>
                        <GridItem xs={12}>
                            {Object.keys(report.errorList).length !== 0 && (
                                <>
                                    <h4 style={{ textTransform: 'uppercase' }}>
                                        <I18n trans={'pricelists:import_report.detected_errors'} />
                                    </h4>
                                    <GridContainer>
                                        {Object.entries(report.errorList).map(([line, errorDetail]) => {
                                            const uniqueErrors: string[] = Array.from(new Set(errorDetail.errors));

                                            return (
                                                <>
                                                    <GridItem xs={1}></GridItem>
                                                    <GridItem xs={2}>
                                                        <I18n trans={'pricelists:import_report.errors_in_line'} valueObject={{ line: line }} />:
                                                    </GridItem>
                                                    <GridItem xs={9}>
                                                        <ul>
                                                            {Array.from(new Set(errorDetail.lineErrors as string[])).map((error, index) => (
                                                                <>
                                                                    <li key={`line_${line}_${index}`}>
                                                                        <I18n trans={`pricelists:import_report.${error}`} />
                                                                    </li>
                                                                    {index < errorDetail.lineErrors.length - 1 && <br />}
                                                                </>
                                                            ))}

                                                            {Object.entries(errorDetail.columnErrors).map(([column, columnErrors]) => (
                                                                <>
                                                                    {Array.from(new Set(columnErrors as string[])).map((error, index) => (
                                                                        <>
                                                                            <li key={`column_${line}_${column}_${index}`}>
                                                                                <I18n
                                                                                    trans={'pricelists:import_report.column'}
                                                                                    valueObject={{ column: column }}
                                                                                />
                                                                                :
                                                                                <I18n trans={`pricelists:import_report.${error}`} />
                                                                            </li>
                                                                            {index < uniqueErrors.length - 1 && <br />}
                                                                        </>
                                                                    ))}
                                                                </>
                                                            ))}
                                                        </ul>
                                                    </GridItem>
                                                </>
                                            );
                                        })}
                                    </GridContainer>
                                </>
                            )}
                        </GridItem>
                    </GridContainer>
                </DialogContent>
                <DialogActions>
                    <CustomTextButtom callback={onClose} callbackParam={{}} label="general:common.close" size="sm" />
                </DialogActions>
            </Dialog>
            {importId &&
                <PricelistUploadFileView open={openFileView} closeDialog={(): void => {
                    setOpenFileView(false);
                }} tariff={tariff} importId={importId} />
            }
        </>
    );
};
